import {Accordion, Button, Badge} from 'react-bootstrap'
import React from 'react'
import {TFormAllValues, TFormMemberPolicy} from '../../../policies/policiesTypes'
import {LegalDocumentAccordionItem} from './sectionsMemberFormsTabs/LegalDocumentAccordionItem'
import {EmploymentIncomeAccordionItem} from './sectionsMemberFormsTabs/EmploymentIncomeAccordionItem'
import {PersonalInformation} from './sectionsMemberFormsTabs/PersonalInformation'
import {SpecificInformationMember} from './sectionsMemberFormsTabs/SpecificInformationMember'
import {FormikErrors, FormikTouched} from 'formik'
import moment from 'moment'
import {formatPhoneDisplay} from 'utils/phoneUtils'
import {COUNTRIES, IMMIGRATION_STATUS_CHOICES} from '../../../../helpers/consts'
import {copyToClipboard} from '../../../../../../utils/clipboardUtils'
import {PhoneProviderActions} from '../../../../components/telephony/PhoneProviderActions'

interface IMembersProps {
  membernum: number
  valuesMembers: TFormMemberPolicy
  allValues: TFormAllValues
  errors: FormikErrors<TFormMemberPolicy>
  touched: FormikTouched<TFormMemberPolicy>
  handleChange: (field: string, value: string | boolean) => void
  handleBlur: (field: string) => void
  removeMember: (index: number) => void
  onSwitchWithHolder: (index: number) => void
  canSwitchHolder: boolean
}

export const MembersEdit: React.FC<IMembersProps> = ({
  membernum,
  valuesMembers,
  allValues,
  handleChange,
  removeMember,
  handleBlur,
  errors,
  touched,
  onSwitchWithHolder,
  canSwitchHolder,
}) => {
  // console.log('errors', errors)

  // Helper functions for preview
  const formatSSN = (ssn: string | null | undefined) => {
    if (!ssn) return ''
    // If already formatted (xxx-xx-xxxx), return as is
    if (ssn.includes('xxx-xx-')) return ssn
    // If full SSN, mask it
    if (ssn.length >= 4) {
      return `xxx-xx-${ssn.slice(-4)}`
    }
    return ssn
  }

  const calculateTotalIncome = () => {
    const income1 = parseFloat(valuesMembers?.annualIncome1 || '0')
    const income2 = parseFloat(valuesMembers?.annualIncome2 || '0')
    const income3 = parseFloat(valuesMembers?.annualIncome3 || '0')
    const total = income1 + income2 + income3
    return total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  }

  const getImmigrationStatusLabel = (status: string | null | undefined) => {
    if (!status) return ''
    const found = IMMIGRATION_STATUS_CHOICES.find(choice => choice.value === status)
    return found?.label || status
  }

  const getCountryLabel = (countryCode: string | null | undefined) => {
    if (!countryCode) return ''
    const found = COUNTRIES.find(country => country.value === countryCode)
    return found?.label || countryCode
  }

  const getGenderIcon = (gender: string | null | undefined) => {
    if (!gender) return 'bi-person'
    const normalized = gender.toLowerCase()
    if (normalized === 'male') return 'bi-gender-male'
    if (normalized === 'female') return 'bi-gender-female'
    return 'bi-gender-ambiguous'
  }

  const memberFullName = [
    valuesMembers?.firstName,
    valuesMembers?.middleName,
    valuesMembers?.lastName,
    valuesMembers?.secondLastName,
  ]
    .filter(Boolean)
    .join(' ')
  const formattedDOB = valuesMembers?.dateBirth ? moment(valuesMembers.dateBirth).format('MM/DD/YYYY') : ''
  const formattedPhone = formatPhoneDisplay(valuesMembers.phone)
  const memberAge = valuesMembers?.dateBirth ? moment().diff(moment(valuesMembers.dateBirth), 'years') : null

  return (
    <div className='member'>
      <style>{`
        .member .accordion-button {
          padding: 0.6rem 1.2rem !important;
          min-height: auto !important;
          line-height: 1.35 !important;
          font-size: 1rem !important;
        }
        .member .accordion-button:not(.collapsed) {
          padding: 0.6rem 1.2rem !important;
        }
        .member .accordion-button::after {
          margin-left: 1.5rem;
        }
        .member .accordion-item {
          margin-bottom: 0 !important;
          border-bottom: 1px solid rgba(0,0,0,.125);
        }
        .member .accordion-item:last-child {
          margin-bottom: 0 !important;
        }
        .member .accordion-item:first-child {
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
        }
        .member .accordion-item:last-child {
          border-bottom-left-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
        }
        .member .accordion-body {
          padding: 1.2rem 1.3rem !important;
        }
      `}</style>
      <Accordion className='my-3'>
        <Accordion.Item eventKey='0'>
          <Accordion.Header>
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: '4px',
                  fontSize: '1.05rem',
                  lineHeight: '1.2',
                  color: '#0d6efd',
                }}
                className='member-name'
              >
                {memberFullName || 'Member'}
              </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1.25rem' }}>
                  {canSwitchHolder && (
                    <Button
                      size='sm'
                      variant='primary'
                      style={{ padding: '0.25rem 0.6rem', lineHeight: 1.25, fontSize: '0.78rem' }}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onSwitchWithHolder(membernum)
                      }}
                    >
                      Switch with the policy holder
                    </Button>
                  )}
                  <Button
                    size='sm'
                    variant='danger'
                    style={{ padding: '0.25rem 0.6rem', lineHeight: 1.25, fontSize: '0.78rem' }}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      removeMember(membernum)
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', flexWrap: 'wrap', gap: '4px 8px', alignItems: 'center', lineHeight: '1.2' }}>
                {valuesMembers?.relationship && (
                  <Badge
                    bg="primary"
                    className="text-white"
                    style={{ fontSize: '0.7rem', padding: '2px 6px', letterSpacing: '0.01em' }}
                  >
                    {valuesMembers.relationship}
                  </Badge>
                )}
                {valuesMembers?.applicant !== undefined && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    {valuesMembers.applicant ? (
                      <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '0.9rem' }}></i>
                    ) : (
                      <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '0.9rem' }}></i>
                    )}
                    <span>{valuesMembers.applicant ? 'Applicant' : 'Not Applicant'}</span>
                  </span>
                )}
                {valuesMembers?.gender && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <i className={`bi ${getGenderIcon(valuesMembers.gender)}`} style={{ fontSize: '0.85rem' }}></i>
                    <span>{valuesMembers.gender}</span>
                  </span>
                )}
                {formattedDOB && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <i className="bi bi-calendar-event" style={{ fontSize: '0.85rem' }}></i>
                    <span>
                      {formattedDOB}
                      {memberAge !== null && ` (Age ${memberAge})`}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      style={{ padding: 0, border: 0, textDecoration: 'none', fontSize: '0.85rem' }}
                      title="Copy date of birth"
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        copyToClipboard(formattedDOB, 'Date of Birth')
                      }}
                    >
                      <i className="bi bi-files text-primary"></i>
                    </Button>
                  </span>
                )}
                {formattedPhone && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <PhoneProviderActions phone={valuesMembers.phone} displayText={formattedPhone} />
                    <Button
                      variant="link"
                      size="sm"
                      style={{ padding: 0, border: 0, textDecoration: 'none', fontSize: '0.85rem' }}
                      title="Copy phone"
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        copyToClipboard(formattedPhone, 'Phone')
                      }}
                    >
                      <i className="bi bi-files text-primary"></i>
                    </Button>
                  </span>
                )}
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <PersonalInformation
              errors={errors}
              touched={touched}
              membernum={membernum}
              isHolder={false}
              valuesMember={valuesMembers}
              allValues={allValues}
              handleChange={handleChange}
              handleBlur={handleBlur}
            />
          </Accordion.Body>
        </Accordion.Item>
        {/* Specific information */}
        <Accordion.Item eventKey='1'>
          <Accordion.Header>
            <div style={{ width: '100%' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '0.95rem', lineHeight: '1.2' }}>
                Specific information
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', flexWrap: 'wrap', gap: '4px 8px', alignItems: 'center', lineHeight: '1.2' }}>
                {valuesMembers?.countryBirth && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <i className="bi bi-globe-americas" style={{ fontSize: '0.85rem' }}></i>
                    <span>{getCountryLabel(valuesMembers.countryBirth)}</span>
                  </span>
                )}
                {valuesMembers?.maritalStatus && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <i className="bi bi-people-fill" style={{ fontSize: '0.85rem' }}></i>
                    <span>{valuesMembers.maritalStatus}</span>
                  </span>
                )}
                {(valuesMembers?.weight || valuesMembers?.height) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <i className="bi bi-person-bounding-box" style={{ fontSize: '0.85rem' }}></i>
                    <span>
                      {valuesMembers?.weight && `${valuesMembers.weight}kg`}
                      {valuesMembers?.weight && valuesMembers?.height && ' / '}
                      {valuesMembers?.height && `${valuesMembers.height}cm`}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <SpecificInformationMember
              membernum={membernum}
              values={valuesMembers}
              handleChange={handleChange}
            />
          </Accordion.Body>
        </Accordion.Item>
        {/* Employment information */}
        <EmploymentIncomeAccordionItem
          eventKey='2'
          values={valuesMembers}
          handleChange={handleChange}
          totalIncome={calculateTotalIncome()}
        />
        {/* Legal documents */}
        <LegalDocumentAccordionItem
          eventKey='3'
          membernum={membernum}
          values={valuesMembers}
          handleChange={handleChange}
          immigrationStatusLabel={getImmigrationStatusLabel(valuesMembers.immigrationStatus)}
          formattedSSN={formatSSN(valuesMembers.ssn)}
        />
      </Accordion>
    </div>
  )
}
